import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import ProfileSummary from '../../components/ProfileSummary';
import ProfileLinkedAccounts from '../../components/ProfileLinkedAccounts';
import GlobalModal from '../../components/GlobalModal';
import AccountPasswordModal from './Modals/AccountPasswordModal';
import RequestVolunteerModal from './Modals/RequestVolunteerModal';
import InviteToLinkModal from './Modals/InviteToLinkModal';
import AccountEmailModal from './Modals/AccountEmailModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import MyAccountOrganisations from '../../components/MyAccountOrganisations';
import DocumentModal from './Modals/DocumentModal';
import AccountPhoneModal from './Modals/AccountPhoneModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { AccountMyAccountAccount, DomainProfileType } from '../../api/models';

const MyAccount: React.FC = () => {
	const dispatch = useAppDispatch();

	// Check Profile Type of the Active User
	const activeProfileType = useAppSelector((state) => state.user.activeProfileType);

	// Get all Qualification Certifications from Redux store
	const qualificationCertifications = useAppSelector((state) => state?.lists.qualificationCertifications);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state and Initial Data
	const initialData = {
		userId: undefined,
		userProfileId: undefined,
		childAwareId: undefined,
		profileImage: undefined,
		firstName: undefined,
		lastName: undefined,
		email: undefined,
		phoneNumber: undefined,
		password: undefined,
		profileType: undefined,
		certifications: [],
		organisations: [],
		linkedAccounts: []
	};

	const [myAccountData, setMyAccountData] = React.useState<AccountMyAccountAccount>(initialData);

	// Set up state for Clean up function
	const [isMounted, setIsMounted] = React.useState<boolean>(false);

	// State to detect whether the image was updated or not
	const [profileUpdate, setProfileUpdated] = React.useState<boolean>(false);

	const [linkedAccountsUpdated, setLinkedAccountsUpdated] = React.useState<boolean>(false);

	const getMyAccountData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountMyAccount();

		imageVersion.current = imageVersion.current + 1;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setMyAccountData(response.result);

			dispatch(UserSlice.actions.update({ profileImage: response.result.profileImage + '&v=' + imageVersion.current }));
		}
	};

	React.useEffect(() => {
		setIsMounted(true);

		getMyAccountData();
	}, []);

	React.useEffect(() => {
		if (profileUpdate && isMounted) {
			getMyAccountData();

			setProfileUpdated(false);
		}
	}, [profileUpdate]);

	React.useEffect(() => {
		if (isMounted && linkedAccountsUpdated) {
			getMyAccountData();
		}
	}, [linkedAccountsUpdated]);

	const {
		userId,
		userProfileId,
		childAwareId,
		profileImage,
		firstName,
		lastName,
		email,
		phoneNumber,
		profileType,
		certifications,
		missingCertifications,
		organisations,
		linkedAccounts
	} = myAccountData;

	// Account Password, Email & Phone Modal
	const [accountPasswordModalShow, setAccountPasswordModalShow] = React.useState<boolean>(false);

	const [accountEmailModalShow, setAccountEmailModalShow] = React.useState<boolean>(false);

	const [accountPhoneModalShow, setAccountPhoneModalShow] = React.useState<boolean>(false);

	const accountDetails = {
		childAwareId,
		email,
		phoneNumber
	};

	// Add New Document Modal
	const [newDocumentModalShow, setNewDocumentsModalShow] = React.useState<boolean>(false);

	const [currentFormModal, setCurrentFormModal] = React.useState<string>('');

	const [formId, setFormId] = React.useState<string>('');

	const addNewDocumentHandler = () => {
		setFormId('');

		setCurrentFormModal('');

		setNewDocumentsModalShow(true);
	};

	const editDocumentHandler = (formId: string, type: string) => {
		setFormId(formId);

		setCurrentFormModal(type);

		setNewDocumentsModalShow(true);
	};

	React.useEffect(() => {
		if (!newDocumentModalShow && isMounted) {
			getMyAccountData();
		}
	}, [newDocumentModalShow]);

	// Check if user is a student
	const isStudent = profileType === DomainProfileType.Student;

	// Request Volunteer Status Modal
	const [requestVolunteerModalShow, setRequestVolunteerModalShow] = React.useState<boolean>(false);

	const [organisationId, setOrganisationId] = React.useState<string>('');

	// Invite to link account
	const [inviteToLinkModalShow, setInviteToLinkModalShow] = React.useState<boolean>(false);

	const imageVersion = React.useRef<any>(1);

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<ProfileSummary
									profileImage={profileImage + '&v=' + imageVersion.current}
									firstName={firstName}
									lastName={lastName}
									canUpdateProfileImage={true}
									isUpdated={setProfileUpdated}
									isStudent={isStudent}
								/>

								{/* ACCOUNT INFO */}
								<section className="info-section">
									<div className="section-header">
										<h2 className="header-name color-dark">Account information</h2>
									</div>

									<div className="row-container">
										<div className="row detail-row">
											<div className="col-11 pdl-0">
												<div className="row detail-info">
													<div className="col-lg-5 pdl-0 detail-name bold">ChildAware ID</div>

													<div className="col-lg-6 pdl-0 detail-status account">{childAwareId}</div>
												</div>
											</div>
										</div>
									</div>

									{!isStudent && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-11 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-5 pdl-0 detail-name bold">Phone Number</div>

														<div className="col-lg-6 pdl-0 detail-status account">{phoneNumber}</div>
													</div>
												</div>

												<div className="col-1 pdr-0 link-group">
													<a className="btn-text" onClick={() => setAccountPhoneModalShow(true)}>
														Edit
													</a>
												</div>
											</div>
										</div>
									)}

									<div className="row-container">
										<div className="row detail-row">
											<div className="col-11 pdl-0">
												<div className="row detail-info">
													<div className="col-lg-5 pdl-0 detail-name bold">Email</div>

													<div className="col-lg-6 pdl-0 detail-status account">{email}</div>
												</div>
											</div>

											<div className="col-1 pdr-0 link-group">
												<a className="btn-text" onClick={() => setAccountEmailModalShow(true)}>
													Edit
												</a>
											</div>
										</div>
									</div>

									<div className="row-container">
										<div className="row detail-row">
											<div className="col-11 pdl-0">
												<div className="row detail-info">
													<div className="col-lg-5 pdl-0 detail-name bold">Password</div>

													<div className="col-lg-6 pdl-0 detail-status account">
														<input
															readOnly
															className="hidden-input"
															type="password"
															name="password"
															id="password"
															value={'password'}
														/>
													</div>
												</div>
											</div>

											<div className="col-1 pdr-0 link-group">
												<a className="btn-text" onClick={() => setAccountPasswordModalShow(true)}>
													Edit
												</a>
											</div>
										</div>
									</div>
								</section>

								{/* DOCUMENTS */}

								<section className="info-section">
									{activeProfileType !== DomainProfileType.Student && (
										<div className="section-header">
											<h2 className="header-name color-dark">Documents</h2>

											<Button
												onClick={() => addNewDocumentHandler()}
												className="btn-text-icon"
												buttonIcon={<PlusIcon className="link-icon" />}
											>
												Add new document
											</Button>
										</div>
									)}

									{activeProfileType === DomainProfileType.Student && certifications && certifications.length > 0 && (
										<div className="section-header">
											<h2 className="header-name color-dark">Documents</h2>
										</div>
									)}

									{certifications &&
										certifications.length > 0 &&
										certifications.map((item, index) => {
											return (
												<div key={index} className="row-container">
													<div className="row detail-row">
														<div className="col-11 pdl-0">
															<div className="row detail-info">
																<div className="col-lg-5 pdl-0 detail-name bold">
																	{qualificationCertifications &&
																	qualificationCertifications.filter(({ label }) => label === item.displayName)[0]
																		? 'Qualification certificate'
																		: item.displayName}
																</div>

																<div
																	className={`col-lg-6 pdl-0 detail-status account ${
																		item.isExpired === true ? 'expired' : null
																	}`}
																>
																	{item.isExpired === true && 'Expired'}

																	{item.isExpired === false &&
																		(qualificationCertifications &&
																		qualificationCertifications.filter(({ label }) => label === item.displayName)[0]
																			? item.displayName
																			: item.displayName === 'Passport'
																			? item.certificationNumber
																			: item.displayName === 'Vaccination Certificate' ||
																			  item.displayName === 'Police Check'
																			? item.fileName
																			: `${item.certificationNumber}, ${item.state}`)}
																</div>
															</div>
														</div>

														<div className="col-1 pdr-0 link-group">
															<Button
																className="btn-text pdr-0"
																onClick={() => {
																	editDocumentHandler(item.id || '', item.displayName || '');
																}}
															>
																Edit
															</Button>
														</div>
													</div>
												</div>
											);
										})}

									{missingCertifications &&
										missingCertifications.length > 0 &&
										missingCertifications.map((item, index) => {
											return (
												<div key={index} className="row-container">
													<div className="row detail-row">
														<div className="col-11 pdl-0">
															<div className="row detail-info">
																<div className="col-lg-5 pdl-0 detail-name bold">{item.name}</div>

																<div className="col-lg-6 pdl-0 detail-status missing ">Missing document</div>
															</div>
														</div>

														<div className="col-1 pdr-0 link-group"></div>
													</div>
												</div>
											);
										})}
								</section>

								{/* ORGANISATIONS */}
								<MyAccountOrganisations
									update={getMyAccountData}
									setRequestVolunteerModalShow={setRequestVolunteerModalShow}
									organisations={organisations}
									setOrganisationId={setOrganisationId}
								/>

								{/* LINKED ACCOUNT */}
								{(profileType === DomainProfileType.Parent || profileType === DomainProfileType.Student) && (
									<ProfileLinkedAccounts
										linkedAccounts={linkedAccounts}
										setLinkedAccountsUpdated={setLinkedAccountsUpdated}
										setClicked={setInviteToLinkModalShow}
										userId={userId}
										profileType={profileType}
									/>
								)}

								<GlobalModal
									isOpen={accountPasswordModalShow}
									titleText="Change password"
									setIsOpen={setAccountPasswordModalShow}
								>
									<AccountPasswordModal accountDetails={accountDetails} />
								</GlobalModal>

								<GlobalModal
									isOpen={accountEmailModalShow}
									titleText="Change Email"
									setIsOpen={setAccountEmailModalShow}
								>
									<AccountEmailModal accountDetails={accountDetails} />
								</GlobalModal>

								<GlobalModal
									isOpen={accountPhoneModalShow}
									titleText="Change Phone Number"
									setIsOpen={setAccountPhoneModalShow}
								>
									<AccountPhoneModal getMyAccountData={getMyAccountData} accountDetails={accountDetails} />
								</GlobalModal>

								<GlobalModal
									isOpen={newDocumentModalShow}
									setIsOpen={setNewDocumentsModalShow}
									titleText={currentFormModal === '' ? 'Add New Document' : currentFormModal}
								>
									<DocumentModal userProfileId={userProfileId} formId={formId} setTitle={setCurrentFormModal} />
								</GlobalModal>

								<GlobalModal
									isOpen={requestVolunteerModalShow}
									setIsOpen={setRequestVolunteerModalShow}
									titleText="Request Volunteer status"
								>
									<RequestVolunteerModal update={getMyAccountData} organisationId={organisationId} />
								</GlobalModal>

								<GlobalModal
									isOpen={inviteToLinkModalShow}
									setIsOpen={setInviteToLinkModalShow}
									titleText="Invite to link accounts"
								>
									<InviteToLinkModal update={getMyAccountData} />
								</GlobalModal>

								<ScrollToTopButton extraClass="mtop-40" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default MyAccount;
